import { apiClient } from "@/services/apiClient";

export type RecurringFrequency = "once" | "monthly";

/**
 * Backend accepts these four kinds. The portal UI only ever writes the
 * three business-day rules — `same_day` is the legacy calendar-day rule
 * we kept so existing schedules created before the simplification keep
 * loading without a migration step.
 */
export type MonthlyDateRule =
  | "first_business_day_of_month"
  | "last_business_day_of_month"
  | "nth_business_day"
  | "same_day";

export interface RecurringScheduleLine {
  description: string;
  quantity: number;
  unitPrice: number;
  taxCategory: string;
}

export interface RecurringInvoiceSchedule {
  id: string;
  companyId: string;
  clientId: string;
  client?: {
    id: string;
    legalName: string;
    displayName?: string;
  };
  currency: "UYU" | "USD";
  amount: number;
  lineDescription: string;
  taxCategory: string;
  /** When set, generated invoices use these lines; otherwise legacy single-line fields apply. */
  lines?: RecurringScheduleLine[] | null;
  frequency: RecurringFrequency;
  /** Present once API returns it; defaults to same-day in the UI. */
  monthlyDateRule?: MonthlyDateRule;
  /** Set when `monthlyDateRule === 'nth_business_day'`; otherwise null. */
  monthlyBusinessDayOrdinal?: number | null;
  nextSendDate: string;
  series: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ListRecurringSchedulesResponse {
  data: RecurringInvoiceSchedule[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateRecurringSchedulePayload {
  clientId: string;
  currency: "UYU" | "USD";
  /** Multi-line template (preferred). When set, amount/lineDescription/taxCategory are not required. */
  lines?: RecurringScheduleLine[];
  /** Legacy single line (qty 1) when `lines` is omitted. */
  amount?: number;
  lineDescription?: string;
  taxCategory?: string;
  frequency: RecurringFrequency;
  monthlyDateRule?: MonthlyDateRule;
  monthlyBusinessDayOrdinal?: number;
  nextSendDate: string;
  series?: string;
}

export type UpdateRecurringSchedulePayload = Partial<CreateRecurringSchedulePayload> & {
  isActive?: boolean;
};

export async function listRecurringSchedules(params: {
  page?: number;
  limit?: number;
}): Promise<ListRecurringSchedulesResponse> {
  const { data } = await apiClient.get<ListRecurringSchedulesResponse>(
    "/recurring-invoice-schedules",
    { params }
  );
  return data;
}

export async function getRecurringSchedule(id: string): Promise<RecurringInvoiceSchedule> {
  const { data } = await apiClient.get<RecurringInvoiceSchedule>(
    `/recurring-invoice-schedules/${id}`
  );
  return data;
}

export async function createRecurringSchedule(
  payload: CreateRecurringSchedulePayload
): Promise<RecurringInvoiceSchedule> {
  const { data } = await apiClient.post<RecurringInvoiceSchedule>(
    "/recurring-invoice-schedules",
    payload
  );
  return data;
}

export async function updateRecurringSchedule(
  id: string,
  payload: UpdateRecurringSchedulePayload
): Promise<RecurringInvoiceSchedule> {
  const { data } = await apiClient.patch<RecurringInvoiceSchedule>(
    `/recurring-invoice-schedules/${id}`,
    payload
  );
  return data;
}

export async function deleteRecurringSchedule(id: string): Promise<void> {
  await apiClient.delete(`/recurring-invoice-schedules/${id}`);
}

export async function issueRecurringNow(
  scheduleId: string,
  issueDate: string,
): Promise<{ invoiceId: string }> {
  const { data } = await apiClient.post<{ invoiceId: string }>(
    `/recurring-invoice-schedules/${scheduleId}/issue-now`,
    { issueDate }
  );
  return data;
}

export async function confirmRecurringOccurrence(occurrenceId: string): Promise<void> {
  await apiClient.post(
    `/recurring-invoice-schedules/occurrences/${occurrenceId}/confirm`
  );
}

export async function getPendingRecurringForInvoice(
  invoiceId: string
): Promise<{ occurrence: { id: string; scheduleId: string; sendDate: string; invoiceId: string | null } | null }> {
  const { data } = await apiClient.get<{
    occurrence: {
      id: string;
      scheduleId: string;
      sendDate: string;
      invoiceId: string | null;
    } | null;
  }>(`/recurring-invoice-schedules/pending-for-invoice/${invoiceId}`);
  return data;
}
